import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const LOGO_URL = '/solnity-icon.png';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  {
    label: 'Services',
    children: [
      { label: 'Global Staffing', path: '/global-staffing' },
      { label: 'IT Staffing', path: '/it-staffing' },
      { label: 'Our Process', path: '/our-process' },
    ],
  },
  {
    label: 'Partnership',
    children: [
      { label: 'Vendor Partnership', path: '/vendor-partnership' },
      { label: 'Hire Talent', path: '/hire-talent' },
    ],
  },
  {
    label: 'Candidates',
    children: [
      { label: 'Join Talent Pool', path: '/join-talent-pool' },
      { label: 'Candidate Profile Marketing', path: '/candidate-profile-marketing' },
    ],
  },
  { label: 'Contact', path: '/contact' },
];

const DropdownMenu = ({ item, isOpen, onToggle, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  return (
    <div ref={ref} className="relative" onMouseEnter={onToggle} onMouseLeave={onClose}>
      <button
        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className="flex items-center gap-1 text-sm font-medium text-[#333333] hover:text-black transition-colors py-2"
        onClick={onToggle}
      >
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 w-60 bg-white border border-[#E5E5E5] shadow-lg py-2 z-40 animate-fade-in">
          {item.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              data-testid={`nav-link-${child.path.slice(1)}`}
              className="block px-5 py-2.5 text-sm text-[#333333] hover:bg-[#F5F5F5] hover:text-black transition-colors"
              onClick={onClose}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileDropdown(null);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      } border-b border-[#E5E5E5]`}
    >
      <div className="sol-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-0.5">
            <img src={LOGO_URL} alt="Solnity" className="h-7 md:h-8 w-auto object-contain" />
            <span className="text-lg font-bold tracking-[0.04em] text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Solnity
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) =>
              item.children ? (
                <DropdownMenu
                  key={item.label}
                  item={item}
                  isOpen={openDropdown === item.label}
                  onToggle={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                  onClose={() => setOpenDropdown(null)}
                />
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.path === '/' ? 'home' : item.path.slice(1)}`}
                  className={`text-sm font-medium transition-colors py-2 ${
                    location.pathname === item.path ? 'text-black' : 'text-[#333333] hover:text-black'
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          {/* Mobile Toggle */}
          <div className="flex items-center gap-4">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-[#E5E5E5] animate-fade-in">
          <div className="sol-container py-4">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="border-b border-[#F5F5F5]">
                  <button
                    data-testid={`mobile-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-[#333333]"
                    onClick={() => setMobileDropdown(mobileDropdown === item.label ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdown === item.label && (
                    <div className="pb-3 pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`mobile-link-${child.path.slice(1)}`}
                          className="block py-2 text-sm text-[#525252] hover:text-black"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-link-${item.path === '/' ? 'home' : item.path.slice(1)}`}
                  className="block py-3 text-sm font-medium text-[#333333] border-b border-[#F5F5F5]"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
